/**
 * Text sanitization utilities — the single home for every sanitizer.
 */

import { PII_EMAIL_PATTERNS, PII_PHONE_PATTERNS, redactPatterns } from './pii-patterns'
import { normalizeVehiclePlate } from './vehicle-document-policy'

/** Strip HTML, invisible Unicode chars, and directional overrides. */
export const sanitizeName = (v: string) =>
  v
    .normalize('NFC')
    .replace(/[\u200B-\u200D\uFEFF]/g, '') // zero-width chars
    .replace(/[\u202A-\u202E\u2066-\u2069]/g, '') // RTL/LTR overrides
    .replace(/<[^>]*>/g, '') // HTML tags
    .replace(/\s+/g, ' ') // collapse whitespace
    .trim()

/** Title Case — capitalize first letter of each word, lowercase rest. Handles accented chars. */
export function titleCase(input: string): string {
  return input
    .toLowerCase()
    .replace(/(?:^|\s)\S/g, (char) => char.toUpperCase())
    .trim()
}

/** Sentence case — capitalize first letter only. */
export function sentenceCase(input: string): string {
  const trimmed = input.trim()
  if (!trimmed) return ''
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
}

/** Normalize plate number — uppercase, strip spaces/hyphens and Salvadoran card year suffixes. */
export function normalizePlate(input: string): string {
  return normalizeVehiclePlate(input)
}

/**
 * Replace emails with [EMAIL] and phone numbers with [PHONE].
 *
 * The patterns live in `pii-patterns.ts` — the same set the chat filter
 * enforces, so a number that is redacted in a message is also redacted in
 * a crash report. Emails run first: an address whose local part looks
 * like a phone number must come out labelled as one email instead of
 * being torn in half by the phone pass.
 */
export function stripPII(text: string): string {
  const withoutEmails = redactPatterns(text, PII_EMAIL_PATTERNS, '[EMAIL]')
  return redactPatterns(withoutEmails.text, PII_PHONE_PATTERNS, '[PHONE]').text
}
